import React, { useState, useEffect, useRef, useContext } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { FilterMatchMode, FilterOperator } from 'primereact/api'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Toolbar } from 'primereact/toolbar'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Toast } from 'primereact/toast'
import { ToastPopUp } from '../shared/utils'
import server from '../../server'
import MainContentLayout from '../Layout/MainContentLayout'
import { formatCurrency } from '../shared/utils'
import ManufacturerOrderDetailsHeader from './ManufacturerOrderDetailsHeader'
import BackButton from '../shared/backButton'
import { InputFieldTemplate } from '../shared/FilterTemplates'
import { AuthenticationContext } from '../../Auth/authentication.context'
import useTableRowsPerPage from '../../hooks/useTableRowsPerPage'
import { getInitialPaginationState } from '../../shared/tablePagination'

const ManufacturerOrderDetails = () => {
  const { orderId } = useParams()
  const order = useLocation()
  const toast = useRef(null)
  const { user } = useContext(AuthenticationContext)
  const { defaultRows, rowsPerPageOptions, applyPageEvent } = useTableRowsPerPage()

  const dt = useRef(null)
  const [orderDetails, setOrderDetails] = useState(null)
  const [state, setState] = useState(() => getInitialPaginationState(user))

  useEffect(() => {
    setState((prev) => ({ ...prev, rows: defaultRows }))
  }, [defaultRows])

  const [orderData, setOrderData] = useState({})
  const [orderHeader, setOrderHeader] = useState(null)
  const [headerLoading, setHeaderLoading] = useState(false)
  const [tableLoading, setTableLoading] = useState(false)
  const [clearBtnDisabled, setClearBtnDisabled] = useState(true)
  const [globalFilterValue, setGlobalFilterValue] = useState('')
  const [status, setStatus] = useState('')
  const [disabled, setDisabled] = useState(false)
  const [details, setDetails] = useState([])
  const [totalExtendedPrice, setTotalExtendedPrice] = useState(null)
  const [newPrice, setNewPrice] = useState(null)
  const [newTotal, setNewTotal] = useState(null)
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)
  const [minOrderBypassAcknowledged, setMinOrderBypassAcknowledged] = useState(false)

  const gpMinOrderPrice = orderDetails?.[0]?.manufacturer?.gpMinOrderPrice ?? 0
  const orderTotal = newPrice ?? 0
  const isBelowManufacturerMinimum =
    gpMinOrderPrice > 0 && orderTotal > 0 && orderTotal < gpMinOrderPrice

  const fetchOrderHeader = async (headerId) => {
    try {
      // Fetch order header from manufacturer orders endpoint to get full data with pharmacy, supplier, etc.
      // Use the same format as ManufacturerOrders.js
      const response = await server.get(`/invoicesHeaders/manufacturer/?orderNo=${orderId}&limit=1&page=1`)
      
      console.log('Fetched order header response:', response.data)
      
      if (response.data && response.data.invoicesHeaders && response.data.invoicesHeaders.length > 0) {
        const orderData = response.data.invoicesHeaders[0]
        console.log('Order header data:', orderData)
        console.log('Order header pharmacy:', orderData.pharmacy)
        console.log('Order header supplier:', orderData.supplier)
        return orderData
      }
      
      console.warn('No orders found in response')
      // Fallback: try to get from the details invoiceHeader if available
      return null
    } catch (error) {
      console.error('Error fetching order header:', error)
      console.error('Error response:', error.response?.data)
      return null
    }
  }

  const getData = () => {
    setTableLoading(true)

    server
      .get(`/invoicesDetails/?orderNumber=${orderId}&limit=1000`)
      .then(async (response) => {
        const orderDetailsData = response.data.details
        if (orderDetailsData && orderDetailsData.length > 0) {
          const invoiceHeader = orderDetailsData[0].invoiceHeader
          if (invoiceHeader?.status === 'Pending') {
            setDisabled(false)
          } else {
            setDisabled(true)
          }
          
          // If we don't have order.state (direct navigation), fetch full order header
          if (!order.state) {
            console.log('No order.state, fetching full order header...')
            setHeaderLoading(true)
            const fullOrderHeader = await fetchOrderHeader(invoiceHeader?.id)
            console.log('Fetched full order header:', fullOrderHeader)
            if (fullOrderHeader) {
              setOrderHeader(fullOrderHeader)
            } else {
              // Fallback to invoiceHeader if fetch fails - but we need to enrich it
              console.log('Using invoiceHeader as fallback, but it may be missing pharmacy/supplier data')
              // Try to at least set basic data
              setOrderHeader({
                ...invoiceHeader,
                pharmacy: invoiceHeader.pharmacy || null,
                supplier: invoiceHeader.supplier || null,
              })
            }
            setHeaderLoading(false)
          } else {
            // Use order.state if available (navigation from list page)
            console.log('Using order.state')
            setOrderHeader(order.state)
          }
          
          const totalExtendedPrice = orderDetailsData.reduce((total, item) => {
            return total + item.extendedPrice
          }, 0)
          const transformedDetails = orderDetailsData.map((item) => ({
            id: item?.id,
            approvedQuantity: item?.requestedQuantity,
            total: item?.extendedPrice,
          }))
          setNewTotal(totalExtendedPrice)
          setDetails([...details, ...transformedDetails])
          setTotalExtendedPrice(totalExtendedPrice)
          setStatus(invoiceHeader?.status)
          setOrderDetails(orderDetailsData)
        }
        setTableLoading(false)
      })
      .catch((error) => {
        setTableLoading(false)
      })
  }

  const handleRequest = (status) => {
    let body
    if (status === 'Cancel') {
      body = { status: 'Cancelled' }
      setCancelLoading(true)
    } else if (status === 'Confirmed') {
      // Filter out the 'total' key from each object in the details array
      setConfirmLoading(true)
      const filteredDetails = details.map((detail) => {
        const { total, ...rest } = detail
        return rest
      })
      body = { status: 'Confirmed', details: filteredDetails }
    }

    server
      .patch(`/invoicesHeaders/${orderDetails[0]?.invoiceHeader?.id}`, body)
      .then((response) => {
        ToastPopUp(toast, 'success', 'Order updated successfully')
        setCancelLoading(false)
        setConfirmLoading(false)
        getData()
      })
      .catch((e) => {
        ToastPopUp(toast, 'error', 'Error', e.response.data.message)
        setCancelLoading(false)
        setConfirmLoading(false)
      })
  }

  useEffect(() => {
    initState()
    getData()
  }, [])

  useEffect(() => {
    setOrderData(order?.state)
  }, [order])

  useEffect(() => {
    checkClearBtn()
  }, [state])

  useEffect(() => {
    if (details) {
      const newTotalPrice = details.reduce((total, item) => {
        return total + item.total
      }, 0)
      setNewPrice(newTotalPrice)
    }
  }, [details])

  useEffect(() => {
    if (!isBelowManufacturerMinimum) {
      setMinOrderBypassAcknowledged(false)
    }
  }, [isBelowManufacturerMinimum])

  const checkClearBtn = () => {
    if (state.sortField || state.sortOrder || globalFilterValue) {
      setClearBtnDisabled(false)
      return
    }
    for (const key in state?.filters) {
      if (key === 'global') {
        continue
      } else if (
        state?.filters[key].constraints[0].value ||
        state?.filters[key]?.constraints[1]?.value
      ) {
        setClearBtnDisabled(false)
        return
      }
    }
    setClearBtnDisabled(true)
  }

  const clearFilter = () => {
    initState()
    setGlobalFilterValue('')
  }

  const onGlobalFilterChange = (e) => {
    const value = e.target.value
    let _filters = { ...state.filters }

    _filters['global'].value = value

    setState((prevState) => {
      return {
        ...prevState,
        filters: _filters,
      }
    })
    setGlobalFilterValue(value)
  }

  const initState = () => {
    setState((prevState) => ({
      ...prevState,
      sortField: '',
      sortOrder: '',
      filters: {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        itemCode: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: FilterMatchMode.STARTS_WITH },
          ],
        },
        'item.itemDescription': {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }],
        },
        shippedQuantity: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        requestedQuantity: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        unitPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
        extendedPrice: {
          operator: FilterOperator.AND,
          constraints: [
            { value: null, matchMode: 'gte' },
            { value: null, matchMode: 'lte' },
          ],
        },
      },
    }))
    setGlobalFilterValue('')
  }

  const handleInputChange = (rowData, value) => {
    const id = rowData?.id
    const existingItemIndex = details.findIndex((item) => item.id === id)
    if (existingItemIndex === -1) {
      setDetails([
        ...details,
        { id, approvedQuantity: value, total: value * rowData.unitPrice },
      ])
    } else {
      if (details[existingItemIndex].approvedQuantity !== value) {
        const updatedDetails = [...details]
        updatedDetails[existingItemIndex].approvedQuantity = value
        updatedDetails[existingItemIndex].total = value * rowData.unitPrice
        setDetails(updatedDetails)
      }
    }
  }

  const getApprovedQuantity = (rowData) => {
    const line = details.find((item) => item.id === rowData?.id)
    return line?.approvedQuantity ?? rowData?.requestedQuantity
  }

  const shippedQuantity = (rowData) => {
    return (
      <>
        {status === 'Pending' ? (
          <InputNumber
            mode='decimal'
            placeholder='Enter a number'
            showButtons
            min={0}
            max={rowData?.requestedQuantity}
            value={getApprovedQuantity(rowData)}
            onChange={(e) => handleInputChange(rowData, e.value ?? 0)}
          />
        ) : (
          rowData?.shippedQuantity
        )}
      </>
    )
  }

  const confirmDisabled =
    orderTotal === 0 ||
    (isBelowManufacturerMinimum && !minOrderBypassAcknowledged)

  // TOOLBAR
  const topLeftToolbarTemplate = () => (
    <Button
      type='button'
      icon='pi pi-filter-slash'
      label='Clear'
      outlined
      onClick={clearFilter}
      disabled={clearBtnDisabled}
    />
  )

  const topRightToolbarTemplate = () => (
    <InputText
      value={globalFilterValue}
      onChange={onGlobalFilterChange}
      placeholder='Keyword Search'
    />
  )

  // BODY TEMPLATES
  const unitPriceBodyTemplate = (order) => formatCurrency(order.unitPrice)

  const extendedPriceBodyTemplate = (order) =>
    formatCurrency(order.requestedQuantity * order.unitPrice)

  // FILTER TEMPLATES
  const itemCodeFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='Molecule'
      icon='pi pi-hashtag'
      type='number'
    />
  )
  const itemDescriptionFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      placehloder='Item Description'
      icon='pi pi-hashtag'
      type='text'
    />
  )

  const shippedQuantityFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='pi pi-hashtag'
      type='number'
    />
  )
  const requestedQuantityFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='pi pi-hashtag'
      type='number'
    />
  )
  const unitPriceFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )
  const extendedPriceFilterElement = (options) => (
    <InputFieldTemplate
      state={options.value}
      stateHandler={(e) => options.filterCallback(e, options.index)}
      matchMode={options.filterModel.matchMode}
      icon='mdi mdi-currency-usd'
      type='number'
    />
  )

  return (
    <>
      <Toast ref={toast} />

      <MainContentLayout
        title={`Order Details #${orderHeader?.supplierOrderNo || order.state?.supplierOrderNo || orderId}`}
        dt={dt}
      >
        <BackButton />

        {orderHeader && (
          <ManufacturerOrderDetailsHeader
            order={orderHeader}
            total={newTotal}
            status={status}
          />
        )}

        <Toolbar
          className='mb-2'
          left={topLeftToolbarTemplate}
          right={topRightToolbarTemplate}
        />
        <DataTable
          ref={dt}
          value={orderDetails}
          paginator
          paginatorTemplate='FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown'
          rowsPerPageOptions={rowsPerPageOptions}
          loading={tableLoading}
          dataKey='id'
          removableSort
          globalFilterFields={[
            'itemCode',
            'unitPrice',
            'extendedPrice',
            'requestedQuantity',
            'item.itemDescription',
          ]}
          emptyMessage='No order details found.'
          filters={state.filters}
          sortField={state.sortField}
          sortOrder={state.sortOrder}
          rows={state.rows}
          first={state.first}
          onPage={(e) => setState((prev) => applyPageEvent(prev, e))}
          onSort={(e) => setState((prev) => applyPageEvent(prev, e))}
          onFilter={(e) => setState((prev) => applyPageEvent(prev, e))}
        >
          <Column
            field='retailUpcCode'
            header='UPC'
            sortable
            filter
            showFilterMenuOptions={false}
            filterElement={itemCodeFilterElement}
          />
          <Column
            field='item.itemDescription'
            header='Item Description'
            sortable
            filter
            showFilterMenuOptions={false}
            filterElement={itemDescriptionFilterElement}
          />

          <Column
            header='Unit Price'
            field='unitPrice'
            body={unitPriceBodyTemplate}
            filterElement={unitPriceFilterElement}
            filter
            sortable
            showFilterMenuOptions={false}
          />
          <Column
            field='extendedPrice'
            header='Extended Price'
            body={extendedPriceBodyTemplate}
            filterElement={extendedPriceFilterElement}
            filter
            sortable
            showFilterMenuOptions={false}
          />
          <Column
            header='Requested Quantity'
            field='requestedQuantity'
            filter
            sortable
            filterElement={requestedQuantityFilterElement}
            showFilterMenuOptions={false}
          />

          <Column
            header='Confirmed Quantity'
            field='shippedQuantity'
            filter={disabled}
            sortable={disabled}
            hidden={status !== 'Cancelled' ? false : true}
            filterElement={shippedQuantityFilterElement}
            showFilterMenuOptions={false}
            body={shippedQuantity}
          />
        </DataTable>
        {status === 'Pending' && (
          <div className='d-flex justify-content-center m-5  flex-wrap'>
            <div style={{ maxWidth: '32rem', width: '100%' }}>
              <div className='d-flex flex-column  mb-3'>
                <div className='d-flex justify-between  gap-5'>
                  <p className='fw-bold text-start px-0 m-0 w-100'>
                    Minimum Amount
                  </p>

                  {formatCurrency(gpMinOrderPrice)}
                </div>
                <div className='d-flex justify-between  gap-5'>
                  <p className='fw-bold text-start px-0 m-0 w-100'>Total</p>
                  {newPrice !== totalExtendedPrice ? (
                    <del>{formatCurrency(totalExtendedPrice)}</del>
                  ) : (
                    <>{formatCurrency(totalExtendedPrice)}</>
                  )}
                </div>
                <div className='d-flex justify-content-end gap-5 '>
                  <p className='fw-bold px-0  m-0 w-100'></p>
                  <p className='fw-bold px-0 m-0'>
                    {newPrice !== totalExtendedPrice ? (
                      <>{formatCurrency(newPrice)}</>
                    ) : null}
                  </p>
                </div>
              </div>
              {isBelowManufacturerMinimum && (
                <div className='alert alert-warning mb-3' role='alert'>
                  <div className='d-flex gap-2'>
                    <i className='pi pi-exclamation-triangle mt-1' aria-hidden />
                    <div>
                      <strong>Below minimum order amount</strong>
                      <p className='mb-2 mt-1 small'>
                        The confirmed total ({formatCurrency(orderTotal)}) is less than
                        the required minimum ({formatCurrency(gpMinOrderPrice)}). You may
                        still confirm this order after acknowledging the exception below.
                      </p>
                      <div className='d-flex align-items-start gap-2'>
                        <Checkbox
                          inputId='min-order-bypass-ack'
                          checked={minOrderBypassAcknowledged}
                          onChange={(e) =>
                            setMinOrderBypassAcknowledged(!!e.checked)
                          }
                        />
                        <label htmlFor='min-order-bypass-ack' className='mb-0 small'>
                          I acknowledge this order is below the minimum order requirement
                          and choose to confirm it anyway.
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div className='d-flex align-items-end justify-content-center gap-2  flex-wrap'>
                <Button
                  type='button'
                  label='Confirm Order'
                  className='text-center'
                  style={{ minWidth: '151px' }}
                  loading={confirmLoading}
                  disabled={confirmDisabled}
                  onClick={() => {
                    handleRequest('Confirmed')
                  }}
                />
                <Button
                  type='button'
                  label='Cancel Order'
                  className='text-center'
                  severity='danger'
                  style={{ minWidth: '151px' }}
                  loading={cancelLoading}
                  onClick={() => {
                    handleRequest('Cancel')
                  }}
                />
              </div>
            </div>
          </div>
        )}
      </MainContentLayout>
    </>
  )
}

export default ManufacturerOrderDetails
